import { useEffect } from 'react';
import { QuestionsPanel } from '../Questions';
import { GamePanel } from '../Game';
import { DigestPanel } from './Digest';
import { DatesPanel } from './Dates';
import { SharePanel } from './Share';
import { SubTabs, SubPanel, type SubTabDef } from '../../components/SubTabs';
import { Tooltip } from '../../ui/Tooltip';
import './questions.css';

/** Deep-link intent from the compose menu (design v1.1 §5.2): Question opens
 *  today's question and focuses the answer. */
export interface UsComposeIntent {
  kind: 'question';
}

/** Us's sub-navigation (card sjFxIbWi3UgN) — same row, same treatment as
 *  Notebook and Life. */
const US_TABS: SubTabDef[] = [
  { key: 'questions', label: 'Questions' },
  { key: 'share', label: 'Share' },
  { key: 'game', label: 'Game' },
  { key: 'dates', label: 'Dates' },
  { key: 'digest', label: 'Digest' },
];

/**
 * Us panel — "Grow together". Each of the three surfaces is its own tab now
 * instead of one long stack: they are three separate activities, and a digest
 * parked below a game round reads as an afterthought of it.
 *
 * The tab state lives in App so a visit back keeps where you were, and so the
 * More menu's quick links can select the tab before scrolling to it (its
 * "Partner digest" link names `#digest-panel`, which only exists while that
 * tab is open).
 *
 * The dead-end Quizzes / Date ideas tiles are gone (TFeLwOAS1EeP) — Quizzes
 * was declined on card IFHpoZY6DpRu and date ideas arrive with card
 * qyhVbyMpcN0B.
 *
 * Mirrors §5.4 of the design doc.
 */
export function UsPanel({
  members,
  intent,
  activeTab,
  onTabChange,
  prefsSignal = 0,
  onPrefsOpened,
}: {
  members: { memberId: string; displayName: string }[];
  intent?: UsComposeIntent | null;
  activeTab: string;
  onTabChange: (key: string) => void;
  /** Card GMat5z0Zxutf: bumps open the Dates preferences segment. */
  prefsSignal?: number;
  /** Called once the overlay has opened, so the signal can be CONSUMED —
   *  otherwise it stays latched and every later remount reopens the sheet. */
  onPrefsOpened?: () => void;
}) {
  useEffect(() => {
    if (intent?.kind === 'question' && activeTab !== 'questions') onTabChange('questions');
  }, [intent, activeTab, onTabChange]);

  return (
    <>
      <SubTabs
        name="us"
        label="Us sections"
        tabs={US_TABS}
        active={activeTab}
        onChange={onTabChange}
        sticky
      />
      <SubPanel name="us" tabKey={activeTab}>

        {activeTab === 'questions' && (
          <>
            {/* First-move copy moved OFF the surface and into a tooltip beside
                the heading (card tFMiqgWdBdqW): the one-liner used to render as
                a visible .tab-hint paragraph here. The ⓘ keeps the old #hint-us
                id so every walk selector still resolves, and it is its own 44px
                target (the tooltip rule from Cibmk2YuSlFB: explain beside the
                control, never in front of it). */}
            <div className="us-heading-row">
              <h2 className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--type-h3-size)', letterSpacing: 'var(--type-tracking-wider)', fontWeight: 400, color: 'var(--color-cream)', marginTop: 0 }}>
                Today's question
              </h2>
              <Tooltip label="One question a day, answered separately and revealed together — you learn something small about each other every day without it feeling like a talk. For example: answer 'what made you laugh this week' and open both answers at once when you have both written.">
                <span id="hint-us" className="pear-tip-trigger" data-tip-for="us-question" title="One question a day, answered separately and revealed together — you learn something small about each other every day without it feeling like a talk. For example: answer 'what made you laugh this week' and open both answers at once when you have both written.">ⓘ</span>
              </Tooltip>
            </div>
            <div id="us-lane" className="us-lane">
              <QuestionsPanel members={members} intent={intent?.kind === 'question' ? { kind: 'question' } : null} />
            </div>
          </>
        )}

        {/* "Something to share" (card a5fbGvPIggaR) — the prompt this tab
            replaced, minus the number: a sentence, a visibility choice, and a
            seat at the weekly meeting if both partners may read it. */}
        {activeTab === 'share' && <SharePanel />}

        {activeTab === 'game' && <GamePanel members={members} />}

        {/* Date Planner Phase A (card ZdYBVP203b8a). */}
        {activeTab === 'dates' && <DatesPanel members={members} prefsSignal={prefsSignal} onPrefsOpened={onPrefsOpened} />}

        {/* Partner digest — card g3ixXFWzhgJY. */}
        {activeTab === 'digest' && <DigestPanel members={members} />}
      </SubPanel>

    </>
  );
}
